import { StyleSheet } from 'react-native';

import type { useTheme } from '../theme';

// Weight stepper bar (sign toggle + magnitude + plates) (Track R — split from loggerStyles.ts; pure data,
// merged by the loggerStyles barrel). Not imported directly.
export function stepperStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    stepperRow: {
      flexDirection: 'row',
      gap: s.s1,
      paddingHorizontal: s.s1,
    },
    stepperButton: {
      flex: 1,
      // ⭐⭐ DS-84 — il PAVIMENTO è 44, misurato non dedotto (banco visivo
      // 14/09, `.visual-bench/lg95-band-360.png`): a 360dp i sette bottoni
      // stavano a 36 d'altezza e il segno a 29 di larghezza.
      // ⛔ `hitSlop={8}` NON bastava e la riga qui sotto che lo affermava era
      // falsa: con `gap: s1` = 2dp le aree allargate di due bottoni adiacenti
      // si SOVRAPPONGONO, quindi un tap al confine può finire sul vicino — +5
      // invece di +1.25, che è un errore di DATI, non di estetica.
      // Regola che decide: a11y è pavimento e vince sull'estetica (prompt.md).
      minHeight: 44,
      minWidth: 44,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface2,
    },
    stepperButtonPressed: {
      opacity: 0.6,
    },
    stepperButtonText: {
      color: p.ink2,
      // sm (12): la label resta piccola per stare proporzionata dentro un
      // bottone che ora è 44×44 (DS-84). ⛔ Non si torna a un font più grande
      // per "riempire": sette celle su una riga a 360dp non hanno margine.
      fontSize: t.sizes.sm,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    stepperButtonSign: {
      // ⚠️ Era `flex: 0.6`, «slightly tighter than the magnitude buttons so the
      // row reads as "[sign] [magnitudes]" instead of six equal cells».
      // Quella lettura NON è più ottenibile stringendo: col pavimento di 44 su
      // `stepperButton` il segno si fermava comunque al minimo, e un flex più
      // basso rubava soltanto spazio agli altri.
      flex: 1,
      // ⭐⭐ DS-85 — il secondo canale non-colore, scelto da Antenore il 14/09:
      // bordo doppio E glifo più grande, tutti e due.
      //
      // Il difetto che cura: nello stato POSITIVO la cella differiva dalle sei
      // magnitudini per due soli toni e un glifo a 12pt. Prima si distingueva
      // perché era più STRETTA, e quella scansione strutturale l'ha consumata
      // il pavimento di 44 di `DS-84` — per la vista ridotta «va letto» costa
      // più di «si vede la forma».
      //
      // ⛔ La terza via che il doc nominava — rinforzare la posizione con lo
      // spacing — è stata scartata prima di arrivare a lui: costa larghezza, e
      // a 320dp la larghezza non c'è (è il residuo aperto di `DS-84`, il
      // settimo bottone tagliato). Queste due non costano un pixel di layout.
      borderWidth: 2,
    },
    // DS-85 — il glifo del segno, l'altra metà del canale. `md` invece di `sm`.
    // ⛔ Non contraddice il commento su `stepperButtonText`: là il divieto di
    // ingrandire riguarda le MAGNITUDINI, che a 360dp non hanno margine di
    // larghezza per una label più lunga. `+` e `−` sono un carattere solo
    // dentro un box da 44, quindi qui il margine c'è.
    stepperButtonTextSign: {
      fontSize: t.sizes.md,
    },
    stepperButtonSignPositive: {
      backgroundColor: p.surface,
      borderColor: p.ink2,
    },
    stepperButtonTextSignPositive: {
      color: p.ink2,
    },
    // Negative (subtract) mode uses a loud amber 'warn' fill — same treatment
    // as the weight-input signChip — so reduced-vision users can tell at a
    // glance that the magnitude buttons will subtract (per
    // feedback_user_visual_accessibility). Off the brand per the colour budget;
    // the ink comes from inkOnWarn, paired to the
    // warn fill per family (IB-11 — inkOnBrand tracks brand500, not warn).
    stepperButtonSignNegative: {
      backgroundColor: p.warn,
      borderColor: p.warn,
    },
    stepperButtonTextSignNegative: {
      color: p.inkOnWarn,
    },
    stepperButtonPlates: {
      // Neutral helper button (off the brand per the colour budget): opens the
      // plate calculator — a utility, not the screen's primary/live action.
      // Surface2 fill + stronger border keeps it tappable without the orange.
      backgroundColor: p.surface2,
      borderColor: p.borderStrong,
      flex: 1.2,
    },
  });
}
